import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_URL } from "../../config/api";

function Experiences() {
  const [selectedItemIndex, setSelectedItemIndex] = useState(0);
  const [experiences, setExperiences] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchExperiences = async (retries = 3, delay = 1000) => {
      try {
        const response = await axios.get(`${API_URL}/experiences`);
        if (response.data && response.data.success) {
          setExperiences(response.data.data || []);
        } else if (Array.isArray(response.data)) {
          setExperiences(response.data);
        }
        setLoading(false);
      } catch (error) {
        if (error.response?.status === 429 && retries > 0) {
          console.log(
            `Rate limited. Retrying in ${delay}ms... (${retries} attempts left)`
          );
          setTimeout(() => fetchExperiences(retries - 1, delay * 2), delay);
          return;
        }
        console.error("Error fetching experiences:", error);
        setLoading(false);
      }
    };

    fetchExperiences();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white">
        <div className="text-white/30 text-sm tracking-wider uppercase">
          Loading...
        </div>
      </div>
    );
  }

  if (experiences.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white">
        <div className="text-white/30 text-sm tracking-wider uppercase">
          No experiences found
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-white py-16 sm:py-24 md:py-32 px-4 sm:px-6 relative overflow-hidden">
      {/* Subtle grid background */}
      <div className="absolute inset-0 opacity-[0.015]">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)`,
            backgroundSize: "60px 60px",
          }}
        ></div>
      </div>

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Section indicator */}
        <div className="flex items-center gap-3 sm:gap-4 mb-10 sm:mb-14 md:mb-16">
          <div className="w-1 h-1 rounded-full bg-white/30"></div>
          <span className="text-white/30 text-xs tracking-[0.2em] sm:tracking-[0.3em] uppercase font-light">
            Experience
          </span>
          <div className="h-[1px] flex-1 bg-gradient-to-r from-white/10 to-transparent"></div>
        </div>

        <div className="flex gap-8 sm:gap-12 md:gap-16 lg:flex-row flex-col">
          {/* ---------- Timeline Navigation ---------- */}
          <div className="lg:w-80 w-full space-y-2 sm:space-y-3">
            {experiences.map((exp, index) => (
              <button
                key={index}
                onClick={() => setSelectedItemIndex(index)}
                className={`w-full text-left p-4 sm:p-5 md:p-6 rounded-xl sm:rounded-2xl transition-all duration-500 group
                  ${
                    selectedItemIndex === index
                      ? "bg-white/[0.04] border border-white/[0.12]"
                      : "bg-white/[0.01] border border-white/[0.04] hover:bg-white/[0.02] hover:border-white/[0.08]"
                  }`}
                style={{
                  animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both`,
                }}
              >
                {/* Period */}
                <div className="flex items-center gap-2 mb-2 sm:mb-3">
                  <div
                    className={`w-1.5 h-1.5 rounded-full transition-all duration-500 ${
                      selectedItemIndex === index
                        ? "bg-white/60 shadow-sm shadow-white/30"
                        : "bg-white/20 group-hover:bg-white/30"
                    }`}
                  ></div>
                  <span
                    className={`text-[10px] sm:text-xs uppercase tracking-wider font-light transition-colors duration-500 ${
                      selectedItemIndex === index
                        ? "text-white/50"
                        : "text-white/30 group-hover:text-white/40"
                    }`}
                  >
                    {exp.period}
                  </span>
                </div>

                {/* Company */}
                <h3
                  className={`text-xs sm:text-sm font-light mb-1 transition-colors duration-500 ${
                    selectedItemIndex === index
                      ? "text-white/70"
                      : "text-white/40 group-hover:text-white/60"
                  }`}
                >
                  {exp.company}
                </h3>

                {/* Location */}
                <p
                  className={`text-[10px] sm:text-xs font-light transition-colors duration-500 ${
                    selectedItemIndex === index
                      ? "text-white/40"
                      : "text-white/20 group-hover:text-white/30"
                  }`}
                >
                  {exp.location}
                </p>

                {/* Selection indicator */}
                <div
                  className={`mt-3 sm:mt-4 h-[1px] bg-gradient-to-r from-white/20 to-transparent transition-opacity duration-500 ${
                    selectedItemIndex === index
                      ? "opacity-100"
                      : "opacity-0 group-hover:opacity-50"
                  }`}
                ></div>
              </button>
            ))}
          </div>

          {/* ---------- Details Panel ---------- */}
          <div className="flex-1">
            <div key={selectedItemIndex} className="animate-fadeIn">
              {/* Title */}
              <div className="mb-6 sm:mb-8">
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-extralight text-white/90 mb-2 sm:mb-3">
                  {experiences[selectedItemIndex].title}
                </h2>

                <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-xs sm:text-sm">
                  <span className="text-white/40 font-light">
                    {experiences[selectedItemIndex].company}
                  </span>
                  <span className="w-1 h-1 rounded-full bg-white/20"></span>
                  <span className="text-white/30 font-light">
                    {experiences[selectedItemIndex].period}
                  </span>
                </div>
              </div>

              {/* Description */}
              <div className="space-y-4 sm:space-y-5">
                {experiences[selectedItemIndex].description.map((item, idx) => (
                  <div
                    key={idx}
                    className="flex gap-3 sm:gap-4 group animate-slideIn"
                    style={{ animationDelay: `${idx * 0.1}s` }}
                  >
                    {/* Bullet point */}
                    <div className="mt-1.5 sm:mt-2">
                      <div
                        className="w-1.5 h-1.5 rounded-full bg-white/20 group-hover:bg-white/30 
                                    transition-colors duration-500"
                      ></div>
                    </div>

                    {/* Text */}
                    <p
                      className="text-white/40 font-light text-sm sm:text-base leading-relaxed group-hover:text-white/50 
                                 transition-colors duration-500"
                    >
                      {item}
                    </p>
                  </div>
                ))}
              </div>

              {/* Additional Info Card */}
              <div
                className="mt-8 sm:mt-10 md:mt-12 p-4 sm:p-5 md:p-6 rounded-xl sm:rounded-2xl bg-white/[0.01] border border-white/[0.04] 
                            backdrop-blur-sm animate-slideIn"
                style={{ animationDelay: "0.3s" }}
              >
                <div className="flex items-center gap-2 sm:gap-3">
                  <svg
                    className="w-4 h-4 sm:w-5 sm:h-5 text-white/20"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  <span className="text-white/40 text-sm sm:text-sm font-light">
                    {experiences[selectedItemIndex].location}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Timeline Progress Indicator */}
        <div className="mt-16 flex items-center gap-3 justify-center">
          {experiences.map((_, index) => (
            <button
              key={index}
              onClick={() => setSelectedItemIndex(index)}
              className="group relative"
            >
              <div
                className={`h-[2px] transition-all duration-500 ${
                  selectedItemIndex === index
                    ? "w-12 bg-white/40"
                    : "w-8 bg-white/10 group-hover:bg-white/20"
                }`}
              ></div>
            </button>
          ))}
        </div>
      </div>

      {/* Floating accent */}
      <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-white/[0.008] blur-[150px] rounded-full"></div>

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(-10px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out;
        }
        
        .animate-fadeInUp {
          animation: fadeInUp 0.6s ease-out both;
        }
        
        .animate-slideIn {
          animation: slideIn 0.5s ease-out both;
        }
      `}</style>
    </div>
  );
}

export default Experiences;
